import React from 'react';
import styled from 'styled-components';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';

interface Step {
  name: string;
  isComplete: boolean;
  isInProgress?: boolean;
  disabled?: boolean;
  onClick?: () => void;
}

interface StepTrackerProps {
  steps: Step[];
  currentStep?: number; // Optional: highlight current step
  style?: React.CSSProperties;
  compact?: boolean;
}

const StepTracker: React.FC<StepTrackerProps> = ({ steps, currentStep, style, compact = false }) => {
  return (
    <Container style={style} $compact={compact}>
      {steps.map((step, index) => (
        <React.Fragment key={index}>
          <StepItem
            type="button"
            disabled={step.disabled}
            onClick={step.onClick}
            $isClickable={Boolean(step.onClick)}
            $compact={compact}
            aria-current={currentStep === index ? 'step' : undefined}
          >
            <StepCircle isComplete={step.isComplete} isCurrent={currentStep === index} $compact={compact}>
              {step.isInProgress ? (
                <CircularProgress
                  size={compact ? 16 : 22}
                  thickness={5}
                  aria-label={`${step.name} in progress`}
                />
              ) : step.isComplete ? (
                <CheckCircleIcon style={{ color: '#4caf50', fontSize: compact ? '18px' : '24px' }} />
              ) : (
                <StepNumber isCurrent={currentStep === index} $compact={compact}>{index + 1}</StepNumber>
              )}
            </StepCircle>
            <StepName isComplete={step.isComplete} isCurrent={currentStep === index} $compact={compact}>
              {step.name}
            </StepName>
          </StepItem>
          {index < steps.length - 1 && (
            <ArrowContainer $compact={compact}>
              <Arrow $compact={compact}>→</Arrow>
            </ArrowContainer>
          )}
        </React.Fragment>
      ))}
    </Container>
  );
};

const Container = styled.div<{ $compact: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${props => props.$compact ? '12px' : '16px'};
  width: 100%;
`;

const StepItem = styled.button<{ $isClickable: boolean; $compact: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${props => props.$compact ? '4px' : '8px'};
  min-width: ${props => props.$compact ? 'auto' : '80px'};
  padding: 0;
  border: 0;
  background: transparent;
  font: inherit;
  cursor: ${props => props.$isClickable ? 'pointer' : 'default'};

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const StepCircle = styled.div<{ isComplete: boolean; isCurrent?: boolean; $compact: boolean }>`
  width: ${props => props.$compact ? '24px' : '32px'};
  height: ${props => props.$compact ? '24px' : '32px'};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${props => {
    if (props.isComplete) return '#e8f5e8';
    if (props.isCurrent) return '#e3f2fd';
    return '#f5f5f5';
  }};
  border: 2px solid ${props => {
    if (props.isComplete) return '#4caf50';
    if (props.isCurrent) return '#2196f3';
    return '#e0e0e0';
  }};
  transition: all 0.2s ease;
`;

const StepNumber = styled.span<{ isCurrent?: boolean; $compact: boolean }>`
  font-size: ${props => props.$compact ? '12px' : '14px'};
  font-weight: bold;
  color: ${props => props.isCurrent ? '#2196f3' : '#666'};
`;

const StepName = styled(Typography)<{ isComplete: boolean; isCurrent?: boolean; $compact: boolean }>`
  font-size: ${props => props.$compact ? '11px' : '12px'} !important;
  font-weight: ${props => props.isComplete || props.isCurrent ? 'bold' : 'normal'} !important;
  color: ${props => {
    if (props.isComplete) return '#4caf50';
    if (props.isCurrent) return '#2196f3';
    return '#666';
  }} !important;
  text-align: center;
  max-width: ${props => props.$compact ? 'none' : '100px'};
  white-space: ${props => props.$compact ? 'nowrap' : 'normal'};
  line-height: 1.2 !important;
`;

const ArrowContainer = styled.div<{ $compact: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ccc;
  font-size: ${props => props.$compact ? '16px' : '20px'};
  font-weight: bold;
`;

const Arrow = styled.span<{ $compact: boolean }>`
  color: #ccc;
  margin-top: ${props => props.$compact ? '-16px' : '-20px'};
`;

export default StepTracker; 